<template>
  <div v-show="visible" class="flex flex-col w-full h-full overflow-hidden  px-4">
    <div v-show="isLoadingNextPage" class="w-full flex flex-1">
      <loader style="z-index: 1801;" />
      <div class="absolute top-0 bottom-0 left-0 right-0 w-full h-full bg-transparent opacity-50" />
    </div>
    <div v-if="!isLoadingNextPage" class="flex flex-col gap-6 w-full">
      <!-- Filter type radios -->
      <div class="flex flex-col gap-2">
        <span class="text-md">
          {{ t('modelCreate.carryoverArticlesMultipleSeasons.filterType') }}
        </span>
        <div class="flex flex-wrap gap-6">
          <label class="flex items-center gap-2">
            <input
              v-model="formModel.filterType"
              type="radio"
              :value="filterTypeEnum.articleNumber"
            >
            <span>{{ t('general.articleNumber') }}</span>
          </label>
          <label class="flex items-center gap-2">
            <input
              v-model="formModel.filterType"
              type="radio"
              :value="filterTypeEnum.modelNumber"
            >
            <span>{{ t('general.modelNumber') }}</span>
          </label>
          <label class="flex items-center gap-2">
            <input
              v-model="formModel.filterType"
              type="radio"
              :value="filterTypeEnum.modelName"
            >
            <span>{{ t('general.modelName') }}</span>
          </label>
        </div>
      </div>

      <!-- Search input + button -->
      <div class="flex items-center  gap-6">
        <div class="flex flex-col w-[85%]">
          <span class="mb-2 text-md">
            {{ t('modelCreate.carryoverArticlesMultipleSeasons.listData') }}
          </span>
          <tx-input
            v-model.trim="formModel.dataList"
            :required="true"
            type="textarea"
            class="w-full h-24 overflow-y-auto"
            clearable
            :text-area-rows="3"
          />
        </div>

        <tx-button
          type="confirm"
          :loading="isLoading"
          :disabled="formModel.dataList.trim() === ''"
          :text="t('general.search')"
          @click="onSearch"
        />
      </div>
    </div>
    <!-- Table container -->
    <div v-show="!isLoadingNextPage" class="flex-grow w-full overflow-x-auto overflow-y-hidden pb-4">
      <tx-data-table
        :clickable-record="true"
        :columns="columns"
        :data="carryoverData || []"
        :item-actions="[]"
        :list-actions="[]"
        :loading="isLoading"
        :item-height="65"
        :allow-refresh="false"
        :visible-columns="visibleColumns"
        :model-path="modelPath"
        :total-column-width="totalColumnWidth"
        @selection-changed="selectionChanged"
      />
    </div>
    <!-- Footer -->
    <div class="flex justify-end flex-shrink-0 px-6 py-4 border-t bg-white">
      <tx-button class="ml-5" :loading="isLoadingNextPage" :disabled="selectedData.length === 0" type="confirm" :text="t('general.next')" @click="nextStep" />
    </div>
  </div>
</template>

<script lang='ts' setup>
import { clone } from 'lodash-es'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type MyArticle from '@/models/myArticle'
import TxButton from '@/shared/components/TxButton.vue'
import utils from '@/services/utils'
import { useUserStore } from '@/store/userData'
import { AttributeType } from '@/models/catalogAttribute'
import TxInput from '@/shared/components/TxInput.vue'
import appConfig from '@/services/appConfig'
import type { ITxDataTableColumn, ITxDataTableVisibleColumn } from '@/shared/components/txDataTable/TxDataTable.types'
import TxDataTable from '@/shared/components/txDataTable'
import { appConstants } from '@/models/constants'
import { getAvailableCarryoverArticles } from '@/api/t1/article'
import { useNotificationStore } from '@/store/notification'
import Loader from '@/shared/components/Loader.vue'
import type CatalogPriceGroup from '@/models/catalogPriceGroup'
import { getCatalogsPriceGroups } from '@/api/t1/catalog'

const props = defineProps<{
  visible: boolean
  currentView: number
}>()
const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'next', payload: Record<string, any>): void
}>()
const { t } = useI18n()
const userStore = useUserStore()
const notificationStore = useNotificationStore()

const filterTypeEnum = {
  articleNumber: 1,
  modelNumber: 2,
  modelName: 3,
}
const formModel = reactive<Record<string, any>>({})
const carryoverData = ref<any[]>([])
const isLoading = ref(false)
const isLoadingNextPage = ref(false)
const modelPath = ref('carryoverArticlesMultipleSeasons')
const totalColumnWidth = ref(1200)
const selectedData = ref<any[]>([])
watch(() => props.currentView, (value) => {
  if (value === 0) {
    init()
  }
})
const dataTableViewAttributes = computed(() => {
  const result: IMyAttribute[] = []
  if (userStore.activeCatalog && userStore.myAttributes) {
    const articleNumberClone = clone(userStore.myAttributes.ArticleNumber)
    articleNumberClone.ReadOnly = true
    result.push(articleNumberClone)
    const modelNumberClone = clone(userStore.myAttributes.ModelNumber)
    modelNumberClone.ReadOnly = true
    result.push(modelNumberClone)
    const articleNameField = Object.assign({}, appConstants.staticFieldTemplate, {
      AttributeType: AttributeType.Nvarchar,
      Creatable: true,
      Editable: true,
      DisplayName: t('general.modelName'),
      SystemName: 'ArticleName',
      IsRequired: true,
      IsSeasonless: true,
      IsModelLevel: true,
      Visible: true,
      readonly: true,
    })
    result.push(articleNameField)

    Object.values(userStore.myAttributes).forEach((attribute) => {
      if (utils.isDefined(attribute) && !attribute.IsStatic && attribute.IsSeasonless) {
        const attributeClone = clone(attribute)
        result.push(attributeClone)
      }
    })
  }
  return result
})
const columns = computed<ITxDataTableColumn[]>(() => {
  const columnsData: ITxDataTableColumn[] = [
    {
      property: '_thumbnailImage',
      title: '',
      width: 100, // default width if not defined
      type: AttributeType.ArticleImage,
      sortable: true, // optional: enable sorting
    } as ITxDataTableColumn,
  ]
  columnsData.push(...dataTableViewAttributes.value.map(attr => ({
    property: attr.SystemName,
    title: attr.DisplayName,
    width: 150, // default width if not defined
    type: attr.AttributeType,
    sortable: true, // optional: enable sorting
  } as ITxDataTableColumn)))
  return columnsData
})
const indexedColumns = computed(() => {
  return columns.value.reduce((accumulator, currentObject) => {
    accumulator[currentObject.property] = currentObject as ITxDataTableColumn
    return accumulator
  }, {} as Record<string, ITxDataTableColumn>)
})
const visibleColumns = computed<ITxDataTableVisibleColumn[]>(() => {
  const availableColumns: ITxDataTableColumn[] = []
  const thumbnailColumn = indexedColumns.value._thumbnailImage
  const colorwayNumberColumn = indexedColumns.value.ArticleNumber
  if (userStore.activeCatalog && userStore.activeCatalog.Config.CarryoverArticleMultipleSeasonsDataTableVisibleAttributes && userStore.activeCatalog.Config.CarryoverArticleMultipleSeasonsDataTableVisibleAttributes.length !== 0) {
    userStore.activeCatalog.Config.CarryoverArticleMultipleSeasonsDataTableVisibleAttributes.forEach((attributeSystemName) => {
      if (indexedColumns.value[attributeSystemName] && attributeSystemName !== '_thumbnailImage' && attributeSystemName !== 'ArticleNumber') {
        availableColumns.push(indexedColumns.value[attributeSystemName])
      }
    })
  }
  else {
    columns.value.forEach((column) => {
      if (indexedColumns.value[column.property] && column.property !== '_thumbnailImage' && column.property !== 'ArticleNumber') {
        availableColumns.push(column)
      }
    })
  }
  availableColumns.unshift(colorwayNumberColumn)
  availableColumns.unshift(thumbnailColumn)
  return availableColumns.map(attr => ({
    property: attr.property,
    order: 1,
    width: 0.175,
  } as ITxDataTableVisibleColumn))
})
function init() {
  formModel.filterType = filterTypeEnum.articleNumber
  formModel.dataList = ''
  carryoverData.value = []
  isLoading.value = false
}
async function onSearch() {
  isLoading.value = true
  carryoverData.value = []
  if (userStore.activeCatalog) {
    let key = 'ArticleNumbers'
    if (formModel.filterType === filterTypeEnum.modelNumber) {
      key = 'ModelNumbers'
    }
    else if (formModel.filterType === filterTypeEnum.modelName) {
      key = 'ModelNames'
    }
    const validData = Array.from(
      new Set(
        formModel.dataList
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean),
      ),
    )
    const payload = {}
    payload[key] = validData
    // payload = { ModelNames: ['pocket'] }
    const response = await getAvailableCarryoverArticles(userStore.activeCatalog.CatalogCode, payload).catch((e) => {
      console.error(e)
      notificationStore.addNotification({ message: t('general.unexpectedError'), type: 'Alert' })
    })
    if (response) {
      carryoverData.value = response.data
    }
    isLoading.value = false
  }
}
function selectionChanged(data: any[]) {
  selectedData.value = data
}
async function nextStep() {
  if (userStore.activeCatalog) {
    isLoadingNextPage.value = true
    const dataMapIdToCatalogCode: Record<number, number> = {}
    const articleExistInCurrentCatalogMap: Record<number, boolean> = {}

    selectedData.value.forEach((data) => {
      dataMapIdToCatalogCode[data.Id] = data.OtherCatalogs[0]
      articleExistInCurrentCatalogMap[data.ModelId] = !data.IsModelCarryover
    })

    const articleIds = Object.keys(dataMapIdToCatalogCode).map(Number)
    const linkedCatalogs = [...new Set(Object.values(dataMapIdToCatalogCode))]
    const linkedCatalogsPrices: Record<number, Record<string, CatalogPriceGroup>> = {}
    const payloadPriceGroups = { Catalogs: linkedCatalogs }
    const catalogPricesResponse = await getCatalogsPriceGroups(payloadPriceGroups)
    if (catalogPricesResponse.data) {
      catalogPricesResponse.data.forEach((dataValue) => {
        if (!linkedCatalogsPrices[dataValue.CatalogCode]) {
          linkedCatalogsPrices[dataValue.CatalogCode] = {}
        }
        linkedCatalogsPrices[dataValue.CatalogCode][dataValue.Name.toString().trim().toLowerCase()] = dataValue as CatalogPriceGroup
      })
    }
    await appConfig.DB!.getMyArticlesByIdsForMultipleSeasons(userStore.activeCatalog, linkedCatalogsPrices, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, articleIds, undefined, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, linkedCatalogs).then((articles) => {
      const validArticles: MyArticle[] = []
      Object.keys(dataMapIdToCatalogCode).forEach((id) => {
        const validArticle = articles.find(article => article.Id === Number(id) && dataMapIdToCatalogCode[id] === article.CatalogCode)
        if (validArticle) {
          validArticles.push(validArticle)
        }
        else {
          console.log('should not happen')
        }
      })
      isLoadingNextPage.value = false
      emit('next', { targetArticles: validArticles, carryoverModelList: articleExistInCurrentCatalogMap, linkedCatalogsPricesForMultipleSeasons: linkedCatalogsPrices })
    }).catch((e) => {
      console.log(e)
      isLoadingNextPage.value = false
      notificationStore.addNotification({ message: t('general.unexpectedError'), type: 'Alert' })
    })
  }
}
init()
</script>
